import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { Button, Form, Nav, Modal, Badge } from 'react-bootstrap'
import { Quill } from 'react-quill'
import _ from 'lodash'
import 'react-quill/dist/quill.snow.css'

import * as Constants from '../../helpers/constants'
import showToastNotification from '../widgets/toastify'
import { ApiHelper } from '../../helpers/apihelper'
import '../../static/css/announcement_bar.css'
import {
  TABS,
  CHAR_LIMITS,
  FONT_SIZES,
  TOOLBAR_CONTAINER,
  TOOLTIP_MAP,
  initialUserState,
  SYSPROP_NAMES,
} from '../announcement_bar/constants'
import AppAnnouncementMessage from '../announcement_bar/AppAnnouncementMessage'
import ConfirmationModal from '../../helpers/confirmationModal'

const SizeStyle = Quill.import('attributors/style/size')
SizeStyle.whitelist = FONT_SIZES
Quill.register(SizeStyle, true)

const LinkBlot = Quill.import('formats/link')
class CopCustomLink extends LinkBlot {
  static create(value) {
    const url = typeof value === 'string' ? value : value.url
    const node = super.create(url)
    const target = typeof value === 'object' ? value.target : '_self'
    if (target === '_blank') {
      node.setAttribute('target', '_blank')
      node.setAttribute('rel', 'noopener noreferrer')
    } else {
      node.removeAttribute('target')
      node.removeAttribute('rel')
    }
    return node
  }

  static formats(node) {
    return {
      url: node.getAttribute('href'),
      target: node.getAttribute('target') || '_self',
    }
  }

  format(name, value) {
    if (name !== 'link') return super.format(name, value)
    if (!value) return super.format(name, value)
    const url = typeof value === 'string' ? value : value.url
    this.domNode.setAttribute('href', CopCustomLink.sanitize(url))
    const target = typeof value === 'object' ? value.target : '_self'
    if (target === '_blank') {
      this.domNode.setAttribute('target', '_blank')
      this.domNode.setAttribute('rel', 'noopener noreferrer')
    } else {
      this.domNode.removeAttribute('target')
      this.domNode.removeAttribute('rel')
    }
  }
}
Quill.register(CopCustomLink, true)

const getPlainTextLength = (html) => {
  if (!html || html === '<p><br></p>') return 0
  return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').length
}

const toInlineHtml = (html) => {
  if (!html) return html
  return html
    .replace(/<p[^>]*>/g, '<span>')
    .replace(/<\/p>/g, '</span>')
    .replace(/<br\s*\/?>/g, '')
    .trim()
}

const CurrentlyLiveCard = ({ loading, liveData, onLoad }) => {
  const message = liveData?.message
  const hasContent = !loading && message && message !== '<p><br></p>'
  const closeBtnColor = _.get(liveData, 'styles.close_btn.color', '')
  const bgColor = _.get(liveData, 'styles.top_container.background', '') || _.get(liveData, 'styles.top_container.backgroundColor', '')
  const dismissable = liveData?.dismissable !== undefined ? liveData.dismissable : true

  return (
    <div className='announcement-current-live mb-4'>
      <div className='announcement-current-live-header'>
        <span className='announcement-live-badge'>
          <span className='announcement-live-dot' />
          Currently Live
        </span>
        {hasContent && (
          <Button 
            variant='outline-primary' 
            size='sm' 
            onClick={onLoad}
          >
            Load into Editor
          </Button>
        )}
      </div>
      {loading ? (
        <div 
          className='announcement-current-live-body text-muted' 
          style={{ fontSize: '13px' }}
        >
          Fetching current live announcement…
        </div>
      ) : hasContent ? (
        <div className='announcement-current-live-body'>
          <div className='announcement-preview-mock-header'>
            <div className='announcement-preview-mock-logo' />
            <div
              className='announcement-preview-bar'
              style={bgColor ? { background: bgColor } : {}}
            >
              <div
                className='announcement-preview-message'
                dangerouslySetInnerHTML={{ __html: message }}
              />
              {dismissable && (
                <span
                  className='announcement-preview-close'
                  style={closeBtnColor ? { color: closeBtnColor } : {}}
                >
                  ✕
                </span>
              )}
            </div>
            <div className='announcement-preview-mock-nav' />
          </div>
          <div className='announcement-current-live-settings'>
            {closeBtnColor && (
              <span className='announcement-live-setting-item'>
                <span 
                  className='announcement-live-swatch' 
                  style={{ background: closeBtnColor }} 
                />
                Close btn: {closeBtnColor}
              </span>
            )}
            {bgColor && (
              <span className='announcement-live-setting-item'>
                <span 
                  className='announcement-live-swatch'
                  style={{ background: bgColor }} 
                />
                Background: {bgColor}
              </span>
            )}
            <span className='announcement-live-setting-item'>
              Dismissable: {dismissable ? 'Yes' : 'No'}
            </span>
          </div>
        </div>
      ) : (
        <div
          className='announcement-current-live-body text-muted'
          style={{ fontSize: '13px' }}
        >
          No announcement is currently live.
        </div>
      )}
    </div>
  )
}

const CopAnnouncementBar = ({ client }) => {
  const [activeTab, setActiveTab] = useState(TABS.USER)
  const [userState, setUserState] = useState(initialUserState)
  const [loading, setLoading] = useState(false)
  const [linkModal, setLinkModal] = useState({ show: false, url: '', newTab: false })
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [currentLiveUser, setCurrentLiveUser] = useState(null)
  const [loadingLive, setLoadingLive] = useState(true)
  const [userPasteError, setUserPasteError] = useState('')
  const userQuillRef = useRef(null)
  const userLastValidDelta = useRef(null)
  const pendingLinkRef = useRef({ quillRef: null, selection: null })
  const userLinkHandlerRef = useRef(null)

  const openLinkModal = useCallback((quillRef) => {
    const quill = quillRef.current?.getEditor()
    if (!quill) return
    const sel = quill.getSelection(true)
    pendingLinkRef.current = { quillRef, selection: sel }
    const format = sel ? quill.getFormat(sel.index, sel.length) : {}
    const existing = format.link
    setLinkModal({
      show: true,
      url: existing ? (typeof existing === 'string' ? existing : existing.url || '') : '',
      newTab: existing ? (typeof existing === 'object' ? existing.target === '_blank' : false) : false,
    })
  }, [])

  userLinkHandlerRef.current = () => openLinkModal(userQuillRef)

  const userQuillModules = useMemo(() => ({
    toolbar: {
      container: TOOLBAR_CONTAINER,
      handlers: { link: () => userLinkHandlerRef.current?.() },
    },
    clipboard: { matchVisual: false },
  }), [])

  const handleLinkInsert = () => {
    const { quillRef, selection } = pendingLinkRef.current
    const quill = quillRef?.current?.getEditor()
    if (quill && selection) quill.setSelection(selection, 'silent')
    if (quill && linkModal.url.trim()) {
      quill.format('link', { url: linkModal.url.trim(), target: linkModal.newTab ? '_blank' : '_self' })
    }
    setLinkModal({ show: false, url: '', newTab: false })
  }

  const handleLinkRemove = () => {
    const { quillRef, selection } = pendingLinkRef.current
    const quill = quillRef?.current?.getEditor()
    if (quill && selection) {
      quill.setSelection(selection, 'silent')
      quill.format('link', false)
    }
    setLinkModal({ show: false, url: '', newTab: false })
  }

  const handleUserMessageChange = (value, _delta, _source, editor) => {
    if (getPlainTextLength(value) > CHAR_LIMITS[TABS.USER]) {
      const quill = userQuillRef.current?.getEditor()
      if (quill) {
        if (userLastValidDelta.current) {
          quill.setContents(userLastValidDelta.current, 'silent')
        } else {
          quill.setText('', 'silent')
        }
      }
      setUserPasteError(`Pasted text exceeds the ${CHAR_LIMITS[TABS.USER]} character limit and was not added.`)
      return
    }
    setUserPasteError('')
    userLastValidDelta.current = editor.getContents()
    setUserState(prev => ({ ...prev, message: value }))
  }

  const handleUserFieldChange = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value
    setUserState(prev => ({ ...prev, [field]: value }))
  }

  const buildUserPayload = () => {
    const payload = {
      name: SYSPROP_NAMES.USER_ANNOUNCEMENT,
      value: {
        message: toInlineHtml(userState.message),
        dismissable: userState.dismissable,
      },
      desc: 'Announcement bar message',
      client_secret: Constants.CLIENT_SECRET,
    }
    const styles = {}
    if (userState.closeBtnColor) {
      styles.close_btn = { color: userState.closeBtnColor }
    }
    if (userState.bgColor) {
      styles.top_container = { background: userState.bgColor }
    }
    if (Object.keys(styles).length > 0) {
      payload.value.styles = styles
    }
    return payload
  }

  const handleSubmit = () => {
    if (!userState.message || userState.message === '<p><br></p>') {
      showToastNotification({ message: 'Message cannot be empty', isSuccess: false })
      return
    }
    setShowConfirmModal(true)
  }

  const handleConfirm = () => {
    const body = buildUserPayload()
    setLoading(true)
    ApiHelper(client, 'UPDATE_SYSPROP').API(client, body)
      .then((response) => {
        if (response.status === Constants.CO_API.SUCCESS) {
          showToastNotification({
            message: 'Announcement bar updated successfully',
            isSuccess: true,
          })
          setUserState(initialUserState)
        } else {
          showToastNotification({
            message: _.get(response, 'error.message', 'Failed to update announcement bar'),
            isSuccess: false,
          })
        }
      })
      .catch((error) => {
        showToastNotification({
          message: _.get(error, 'response.data.error.message') ||
            _.get(error, 'response.data.message') ||
            'Something went wrong',
          isSuccess: false,
        })
      })
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    const toolbar = document.querySelector('.announcement-editor-section .ql-toolbar')
    if (!toolbar) return
    toolbar.querySelectorAll('button, .ql-picker').forEach((el) => {
      const match = Object.keys(TOOLTIP_MAP).find((cls) => el.classList.contains(cls))
      if (match) el.setAttribute('data-tooltip', TOOLTIP_MAP[match])
    })
  }, [activeTab])

  useEffect(() => {
    setLoadingLive(true)
    ApiHelper(client, 'GET_USER_ANNOUNCEMENT').API(client, { name: SYSPROP_NAMES.USER_ANNOUNCEMENT, client_secret: Constants.CLIENT_SECRET })
      .then((response) => {
        const ann = _.get(response, 'data.value', null)
        setCurrentLiveUser(ann)
      })
      .catch(() => setCurrentLiveUser(null))
      .finally(() => setLoadingLive(false))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleLoadLiveUser = () => {
    if (!currentLiveUser) return
    setUserState({
      message: currentLiveUser.message || '',
      dismissable: currentLiveUser.dismissable !== undefined ? currentLiveUser.dismissable : true,
      closeBtnColor: _.get(currentLiveUser, 'styles.close_btn.color', ''),
      bgColor: _.get(currentLiveUser, 'styles.top_container.background', '') ||
        _.get(currentLiveUser, 'styles.top_container.backgroundColor', ''),
    })
    setUserPasteError('')
  }

  const isEmptyMessage = (msg) => !msg || msg === '<p><br></p>'
  const showPreview = !isEmptyMessage(userState.message) ||
    (userState.bgColor || userState.closeBtnColor)

  return (
    <>
      <h1 className='header'>Announcement Bar Update</h1>
      <div className='announcement-bar-wrapper'>
        <p className='heading-subtext'>
          Update sticky note content and improve user experience across the ClearoutPhone app
        </p>
        <Nav
          variant='tabs'
          activeKey={activeTab}
          onSelect={setActiveTab}
          className='mb-4'
        >
          <Nav.Item>
            <Nav.Link eventKey={TABS.USER}>App Announcement Message</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey={TABS.PUBLIC}>
              Website Announcement Message{' '}
              <Badge
                bg='secondary'
                style={{ fontSize: '10px', verticalAlign: 'middle' }}
              >
                Coming Soon
              </Badge>
            </Nav.Link>
          </Nav.Item>
        </Nav>
        {activeTab === TABS.USER && (
          <CurrentlyLiveCard
            loading={loadingLive}
            liveData={currentLiveUser}
            onLoad={handleLoadLiveUser}
          />
        )}
        {activeTab === TABS.USER && (
          <AppAnnouncementMessage
            userState={userState}
            userQuillRef={userQuillRef}
            userQuillModules={userQuillModules}
            handleUserMessageChange={handleUserMessageChange}
            handleUserFieldChange={handleUserFieldChange}
            setUserState={setUserState}
            getPlainTextLength={getPlainTextLength}
            pasteError={userPasteError}
          />
        )}
        {activeTab === TABS.PUBLIC && (
          <div
            className='d-flex flex-column align-items-center justify-content-center py-5'
            style={{ border: '1px dashed #ccc', borderRadius: 8, backgroundColor: '#fafafa', minHeight: 200 }}
          >
            <i
              className='fa-solid fa-bullhorn'
              style={{ fontSize: 32, color: '#bbb', marginBottom: 12 }}
            />
            <p
              className='mb-1 fw-semibold'
              style={{ color: '#555', fontSize: '16px' }}
            >
              Website Announcement — Coming Soon
            </p>
            <p
              className='text-muted mb-0'
              style={{ fontSize: '13px' }}
            >
              Support for ClearoutPhone website announcements will be available in a future update.
            </p>
          </div>
        )}
        {activeTab === TABS.USER && showPreview && (
          <div className='announcement-preview-section mt-4'>
            <p
              className='fw-semibold mb-2'
              style={{ fontSize: '14px', color: '#555' }}
            >
              Preview
            </p>
            <div className='announcement-preview-mock-header'>
              <div className='announcement-preview-mock-logo' />
              <div
                className='announcement-preview-bar'
                style={{ background: userState.bgColor || undefined }}
              >
                {!isEmptyMessage(userState.message) && (
                  <div
                    className='announcement-preview-message'
                    dangerouslySetInnerHTML={{ __html: toInlineHtml(userState.message) }}
                  />
                )}
                {userState.dismissable && (
                  <span
                    className='announcement-preview-close'
                    style={userState.closeBtnColor ? { color: userState.closeBtnColor } : {}}
                  >
                    ✕
                  </span>
                )}
              </div>
              <div className='announcement-preview-mock-nav' />
            </div>
            <p className='announcement-preview-hint'>Appears centered in the top navigation bar</p>
          </div>
        )}
        {activeTab === TABS.USER && (
          <div className='announcement-bar-submit mt-4'>
            <Button
              variant='outline-success'
              onClick={handleSubmit}
              disabled={loading || isEmptyMessage(userState.message)}
            >
              {loading ? 'Publishing...' : 'Publish Announcement Bar'}
            </Button>
          </div>
        )}
      </div>
      <Modal
        show={linkModal.show}
        onHide={() => setLinkModal(m => ({ ...m, show: false }))}
        centered
        size='sm'
      >
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: '16px' }}>Insert Link</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className='mb-3'>
            <Form.Label className='mb-1'>URL</Form.Label>
            <Form.Control
              type='url'
              placeholder='https://example.com'
              value={linkModal.url}
              onChange={(e) => setLinkModal(m => ({ ...m, url: e.target.value }))}
              autoFocus
              onKeyDown={(e) => { if (e.key === 'Enter' && linkModal.url.trim()) handleLinkInsert() }}
            />
          </Form.Group>
          <Form.Check
            type='switch'
            id='cop-link-modal-new-tab'
            label='Open in new tab'
            checked={linkModal.newTab}
            onChange={(e) => setLinkModal(m => ({ ...m, newTab: e.target.checked }))}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant='outline-danger'
            size='sm'
            onClick={handleLinkRemove}
          >
            Remove
          </Button>
          <Button
            variant='outline-secondary'
            size='sm'
            onClick={() => setLinkModal(m => ({ ...m, show: false }))}
          >
            Cancel
          </Button>
          <Button
            variant='outline-success'
            size='sm'
            onClick={handleLinkInsert}
            disabled={!linkModal.url.trim()}
          >
            Insert
          </Button>
        </Modal.Footer>
      </Modal>
      <ConfirmationModal
        show={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        title='Publish Announcement Bar'
        message='Are you sure you want to publish this announcement message?'
        onConfirm={handleConfirm}
        confirmText='Yes, Publish'
        cancelText='No'
      />
    </>
  )
}

export default CopAnnouncementBar
